import React, { useState, useEffect, useRef } from "react";
import { FaUtensils, FaMapMarkerAlt, FaCity, FaCalendarAlt } from 'react-icons/fa';
import "../styles/JourneyTimeline.css";


const JourneyTimeline = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [counters, setCounters] = useState({
    meals: 0,
    locations: 0,
    cities: 0,
    years: 2009
  });
  
  const sectionRefs = useRef([]);
  const countersStarted = useRef(false);

  // Client data with logos
  const featuredClients = [
    { name: "KINET RAILWAY SOLUTION LTD.", logo: "/newlyaddedclients/Kinet-Railway-Solutions-1.png" },
    { name: "NELSON GLOBAL INDIA LTD.", logo: "/clients/Nelson-Global-1.png" },
    { name: "PRESICE CHEMI PHARMA LTD.", logo: "/clients/Precise-chemi-2.png" },
    { name: "SUZLON ENERGY LTD.", logo: "/clients/Suzlon-3.png" },
    { name: "EVEREST INDIA INDUSTRIES PVT. LTD.", logo: "/clients/Everest-industries-4.png" },
    { name: "ZOBELE INDIA PVT. LTD.", logo: "/clients/Zobele-7.png" },
    { name: "MENON AND MENON LTD.", logo: "/clients/Menon-and-menon-8.png" },
    { name: "KIRLOSKAR BROTHERS LTD.", logo: "/clients/Kirloskar-brothers-9.png" },
    { name: "POLYCAB WIRES PVT LTD.", logo: "/clients/polycab-wires-13.png" }
  ];

  const newClients = [
    { name: "KINET RAILWAY SOLUTION LTD.", logo: "/newlyaddedclients/Kinet-Railway-Solutions-1.png", location: "Latur, Maharashtra" },
    { name: "MANTRI METALLICS PVT LTD", logo: "/newlyaddedclients/Mantri-metallics.png", location: "Mumbai, Kagal - MIDC" },
     { name: "NIRMAL POLY PLAST PVT. LTD", location: "Angam (Unit-1), Gujarat", persons: 500, logo: "/newlyaddedclients/Nirmal-Poly-Plastic.png", industry: "Manufacturing industry" },
    { name: "PAPRO INDUSTRIES", location: "Sanjan, Gujarat", persons: 200, logo: "/newlyaddedclients/Papro-industries.png", industry: "Industrial" }
  ];

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionIndex = parseInt(entry.target.dataset.section);
            setVisibleSections(prev => new Set([...prev, sectionIndex]));
            
            if (sectionIndex === 5 && !countersStarted.current) {
              startCounters();
              countersStarted.current = true;
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Counter animation
  const startCounters = () => {
    let mealsCount = 0;
    const mealsInterval = setInterval(() => {
      mealsCount += 243;
      if (mealsCount >= 17500) {
        mealsCount = 17500;
        clearInterval(mealsInterval);
      }
      setCounters(prev => ({ ...prev, meals: mealsCount }));
    }, 20);
    let locationsCount = 0;
    const locationsInterval = setInterval(() => {
      locationsCount += 1;
      if (locationsCount >= 25) {
        locationsCount = 25;
        clearInterval(locationsInterval);
      }
      setCounters(prev => ({ ...prev, locations: locationsCount }));
    }, 80);
    let citiesCount = 0;
    const citiesInterval = setInterval(() => {
      citiesCount += 1;
      if (citiesCount >= 16) {
        citiesCount = 16;
        clearInterval(citiesInterval);
      }
      setCounters(prev => ({ ...prev, cities: citiesCount }));
    }, 100);
  };

  return (
    <div className="lhs-journey-timeline">
      <h2 className="lhs-journey-title">Our Journey</h2>

      <div className="lhs-timeline-line"></div>

      {/* Section 1: Hero - Left */}
      <section 
        className={`lhs-timeline-section lhs-section-left ${visibleSections.has(1) ? 'lhs-visible' : ''}`}
        data-section="1"
        ref={el => sectionRefs.current[1] = el}
      >
        <div className="lhs-timeline-dot">
          <div className="lhs-dot-icon">🏆</div>
        </div>
        <div className="lhs-section-content lhs-hero-content">
          <div className="lhs-trust-badge">Since 2009</div>
          <h2>Built on Trust Since Day One</h2>
          <p>
            Our journey began in 2009 with Precise Chemi Pharma Ltd., our very first client - and we're proud to say, they're still with us today. This enduring relationship is not just a testament to our service quality. It reflects the trust, consistency, and dedication that define everything we do.
          </p>
        </div>
      </section>

      {/* Section 2: First Client - Right */}
      <section 
        className={`lhs-timeline-section lhs-section-right ${visibleSections.has(2) ? 'lhs-visible' : ''}`}
        data-section="2"
        ref={el => sectionRefs.current[2] = el}
      >
        <div className="lhs-timeline-dot">
          <div className="lhs-dot-icon">🤝</div>
        </div>
        <div className="lhs-section-content lhs-client-content">
          <div className="lhs-client-header">
            <img src="/clients/Precise-chemi-2.png" alt="Precise Chemi Pharma" className="lhs-client-logo" />
            <div className="lhs-first-client-badge">Our First Client</div>
          </div>
          <h2>Precise Chemi Pharma Ltd.</h2>
          <div className="lhs-location-info">
            <FaMapMarkerAlt /> <span>Dindori, Maharashtra</span>
          </div>
          <p>
            Our association with Precise Chemi Pharma showcases our ability to evolve with our clients' growing needs while maintaining unwavering standards in hygiene, taste, consistency and trust.
          </p>
        </div>
      </section>
      
      {/* Section 3: Industry Leader - Left */}
      <section 
        className={`lhs-timeline-section lhs-section-left ${visibleSections.has(3) ? 'lhs-visible' : ''}`}
        data-section="3"
        ref={el => sectionRefs.current[3] = el}
      >
        <div className="lhs-timeline-dot">
          <div className="lhs-dot-icon">⚡</div>
        </div>
        <div className="lhs-section-content lhs-industry-content">
          <div className="lhs-client-header">
            <img src="/clients/Suzlon-3.png" alt="Suzlon Energy" className="lhs-client-logo" />
            <div className="lhs-industry-badge">Industry Leader</div>
          </div>
          <h2>Trusted by Suzlon Energy Ltd.</h2>
          <div className="lhs-suzlon-stats">
            <div className="lhs-stat-item">
              <span className="lhs-stat-number">7+</span>
              <span className="lhs-stat-label">Locations</span>
            </div>
            <div className="lhs-stat-item">
              <span className="lhs-stat-number">7200+</span>
              <span className="lhs-stat-label">People Served</span>
            </div>
          </div>
          <p>
          As a trusted partner to one of India's leading renewable energy providers, we consistently meet Suzlon's highest standards and their trust by serving over 7200 people across 7+ locations nationwide.
          </p>
        </div>
      </section>

      {/* Section 4: Geographic Expansion - Right */}
      <section 
        className={`lhs-timeline-section lhs-section-right ${visibleSections.has(4) ? 'lhs-visible' : ''}`}
        data-section="4"
        ref={el => sectionRefs.current[4] = el}
      >
        <div className="lhs-timeline-dot">
          <div className="lhs-dot-icon">🗺️</div>
        </div>
        <div className="lhs-section-content lhs-expansion-content">
          <h2>Multi-State Expansion</h2>
          <p>From local beginnings to a national presence across India's industrial corridors.</p>
          <div className="lhs-states-grid">
            <div className="lhs-state-card">
              <span className="lhs-state-name">Maharashtra</span>
              <span className="lhs-state-count">12+ locations</span>
            </div>
            <div className="lhs-state-card">
              <span className="lhs-state-name">Daman</span>
              <span className="lhs-state-count">7+ locations</span>
            </div>
            <div className="lhs-state-card">
              <span className="lhs-state-name">Gujarat</span>
              <span className="lhs-state-count">4+ locations</span>
            </div>
            <div className="lhs-state-card">
              <span className="lhs-state-name">Telangana</span>
              <span className="lhs-state-count">1+ location</span>
            </div>
            <div className="lhs-state-card">
              <span className="lhs-state-name">Andhra Pradesh</span>
              <span className="lhs-state-count">1+ location serving 2000 people</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Scale Achievement - Left */}
      <section 
        className={`lhs-timeline-section lhs-section-left ${visibleSections.has(5) ? 'lhs-visible' : ''}`}
        data-section="5"
        ref={el => sectionRefs.current[5] = el}
      >
        <div className="lhs-timeline-dot">
          <div className="lhs-dot-icon">📊</div>
        </div>
        <div className="lhs-section-content lhs-stats-content">
          <h2>Powering India's Industries</h2>
          <p>
            Our growth is measured by the trust of our clients. We proudly deliver thousands of fresh meals daily.
          </p>
          <div className="lhs-stats-grid">
            <div className="lhs-stat-card">
              <FaUtensils className="lhs-counter-icon" />
              <div className="lhs-counter-text">
                <div className="lhs-counter-number">{counters.meals.toLocaleString()}+</div>
                <div className="lhs-counter-label">Meals Served Daily</div>
              </div>
            </div>
            <div className="lhs-stat-card">
              <FaMapMarkerAlt className="lhs-counter-icon" />
              <div className="lhs-counter-text">
                <div className="lhs-counter-number">{counters.locations}+</div>
                <div className="lhs-counter-label">Client Locations</div>
              </div>
            </div>
            <div className="lhs-stat-card">
              <FaCity className="lhs-counter-icon" />
              <div className="lhs-counter-text">
                <div className="lhs-counter-number">{counters.cities}+</div>
                <div className="lhs-counter-label">Cities Served</div>
              </div>
            </div>
            <div className="lhs-stat-card">
              <FaCalendarAlt className="lhs-counter-icon" />
              <div className="lhs-counter-text">
                <div className="lhs-counter-number">{counters.years}</div>
                <div className="lhs-counter-label">Serving Since</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: New Partnerships - Right */}
      <section 
        className={`lhs-timeline-section lhs-section-right ${visibleSections.has(6) ? 'lhs-visible' : ''}`}
        data-section="6"
        ref={el => sectionRefs.current[6] = el}
      >
        <div className="lhs-timeline-dot">
          <div className="lhs-dot-icon">✨</div>
        </div>
        <div className="lhs-section-content lhs-new-clients-content">
          <div className="lhs-new-badge">Latest Additions</div>
          <h2>New Partnerships</h2>
          <p>We're thrilled to welcome these industry leaders to our esteemed clientele.</p>
          <div className="lhs-new-clients-showcase">
            {newClients.map((client, index) => (
              <div key={index} className="lhs-new-client-card">
                <img src={client.logo} alt={client.name} className="lhs-new-client-logo" />
                <div className="lhs-new-client-info">
                  <h4>{client.name}</h4>
                  <div className="lhs-location-info">
                    <FaMapMarkerAlt /> <span>{client.location}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: Client Portfolio - Full Width */}
      <section 
        className={`lhs-timeline-section lhs-section-full ${visibleSections.has(7) ? 'lhs-visible' : ''}`}
        data-section="7"
        ref={el => sectionRefs.current[7] = el}
      >
        <div className="lhs-timeline-dot">
          <div className="lhs-dot-icon">🏢</div>
        </div>
        <div className="lhs-section-content lhs-portfolio-content">
          <h2>Proudly Serving Industry Titans</h2>
          <p>Our portfolio features leading names across multiple industries who expect nothing but the best.</p>
          <div className="lhs-clients-grid">
            {featuredClients.map((client, index) => (
              <div key={index} className="lhs-client-card">
                <img src={client.logo} alt={client.name} className="lhs-portfolio-logo" />
                <div className="lhs-client-name">{client.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline End Marker */}
      <div className="lhs-timeline-end">
        <div className="lhs-timeline-dot lhs-end-dot">
          <div className="lhs-dot-icon">🎯</div>
        </div>
      </div>

    </div>
  );
};

export default JourneyTimeline;